import { trpc } from "@client/trpc";
import { useImpersonationRefValue } from "@client/impersonationRef";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from "react";
import { type AppDrawerName } from "@client/utils/exclusiveDrawer";
import { usePageAttention } from "./usePageAttention";
import { type ChecklistMarkRow } from "@lib/todoRailTypes";
import { todoChecklistQueryInput } from "./todoQueryInput";
import { useCanUseTodoRail } from "./useCanUseTodoRail";
import { useTodoRailSync } from "./TodoRailSyncProvider";

/**
 * How long a surface must stay on screen before it counts as seen.
 *
 * Mount alone was too generous: scrolling past a profile summary marked it
 * read. Three seconds is enough to separate a scroll-by from a look without
 * making a genuine read feel withheld — and any deliberate engagement (see
 * `recordNow`) short-circuits it entirely.
 */
export const TODO_OBSERVATION_DWELL_MS = 3_000;

/**
 * Whether the detector should still be watching for this observation.
 *
 * Only a row that says DONE stops it. A `done: false` row is the funder having
 * RESET the step ("I haven't read this yet"), so the detector re-arms and can
 * tick it again once they really do read it. Treating any existing row as final
 * made the row menu a trapdoor — un-mark, read the thing properly, and the rail
 * stayed wrong with no way back.
 *
 * Extracted from the hook because this one predicate is the whole rule, and it
 * has been wrong twice.
 */
export function shouldWatchForObservation({
  itemKey,
  loaded,
  marks,
  impersonating = false,
}: {
  itemKey: string | null;
  /** False until the checklist has loaded — firing before it arrives would
   *  write on every mount until the first fetch resolved. */
  loaded: boolean;
  marks: readonly ChecklistMarkRow[];
  /** An SFC admin viewing as someone else writes nobody's progress — not the
   *  funder's, and not their own once the session ends. It belongs in the
   *  predicate rather than at the call site because the answer here is what
   *  decides whether an unrecordable report is REMEMBERED, and remembering one
   *  made while impersonating is exactly how it later replayed as the admin's
   *  own. */
  impersonating?: boolean;
}): boolean {
  if (itemKey == null || !loaded || impersonating) return false;
  return !marks.some((mark) => mark.itemKey === itemKey && mark.done);
}

/** What to do with a report that a surface was seen. */
export type ObservationDisposition = "write" | "remember" | "drop";

/**
 * The one place that decides the fate of a report, because this decision has
 * been wrong four times — each through a different early return, and each time
 * the symptom was the row menu looking broken.
 *
 * - `drop` when the answer is known and it is no: the step is already done, or
 *   an SFC admin is viewing as someone else and may write nobody's progress.
 * - `remember` ONLY while there is no durable queue yet (boot). That is the
 *   sole case where the report can neither be written nor judged, and the
 *   latch that holds it is why a pyramid restoring its level before the
 *   outbox mounts is not simply lost.
 * - `write` otherwise — INCLUDING when the checklist has not loaded. Waiting
 *   for it looks safer and is not: the report is a thing the funder just did,
 *   the outbox is durable, and the server's last-write-wins settles a
 *   duplicate. Holding it in a component-local ref meant navigating to the
 *   next recommender before a slow (or offline) checklist answered threw the
 *   engagement away.
 */
export function dispositionForObservation({
  hasOutbox,
  loaded,
  watching,
  impersonating,
}: {
  hasOutbox: boolean;
  loaded: boolean;
  /** `shouldWatchForObservation`'s answer — only meaningful once loaded. */
  watching: boolean;
  impersonating: boolean;
}): ObservationDisposition {
  if (impersonating) return "drop";
  if (!hasOutbox) return "remember";
  if (loaded && !watching) return "drop";
  return "write";
}

/**
 * Records that the funder has actually SEEN something the rail tracks.
 *
 * The two marks this writes — a recommender's general round notes, and their
 * profile summary — started life as checkboxes the funder ticked by hand. That
 * was wrong: a checklist you have to maintain is the bookmark-management chore
 * this whole feature exists to remove, and a tick is a claim about what you
 * read rather than a record of it. The app already knows when those surfaces
 * were opened, so it should say so itself.
 *
 * **"Seen" has two signals, and either is enough:**
 * - the surface stayed mounted for {@link TODO_OBSERVATION_DWELL_MS}, or
 * - the caller reports deliberate engagement via the returned `recordNow`.
 *
 * A caller passes `dwellMs: null` to opt out of the first and demand the
 * second. That is right wherever the surface shows something at rest: a
 * pyramid summary's first layer is one line, visible without any action, so
 * time on screen there says nothing about whether it was read.
 *
 * Mount alone is NOT the signal, and mount + dwell is not enough either.
 * `DeferredUntilNearViewport` mounts a section ~600px early, and its `eager`
 * escape hatch force-mounts EVERY deferred section during Back/Forward scroll
 * restoration — so a funder pressing Back had notes three screens away marked
 * read while they sat still. A caller that can be off-screen therefore passes
 * `observe`, and the dwell only runs while that element is actually on screen,
 * the tab is foregrounded, and no drawer is covering the page.
 *
 * Idempotent while the mark stands: once the row says done it never writes
 * again, so a mounted summary doesn't fire a mutation per render.
 */
export function useRecordTodoObservation(
  roundId: string,
  itemKey: string | null,
  options?: {
    dwellMs?: number | null;
    /**
     * Element that must be VISIBLE for the dwell to run. Omit only where being
     * mounted already means being looked at (the notes drawer, which is a modal
     * showing one recommender).
     */
    observe?: RefObject<Element | null>;
    /**
     * The drawer this surface lives INSIDE, if any.
     *
     * The dwell pauses while a drawer covers the page — but a caller rendered
     * within a drawer is not covered by it, it IS it. Without this the General
     * Round Notes drawer blocked its own mark: the one surface whose whole job
     * is recording "read the round notes" could never record it.
     */
    hostDrawer?: AppDrawerName;
  },
): { recordNow: () => void } {
  const viewAsUserId = useImpersonationRefValue();
  const utils = trpc.useUtils();
  const queryInput = todoChecklistQueryInput(roundId, viewAsUserId);
  // Gated on the viewer having a rail AND a target. Target alone wasn't
  // enough: the notes drawer is mounted in the round shell for every member, so
  // a recommender switching its Viewing selector — or a restricted funder
  // opening it — produced a target and then burned React Query's default
  // retries on a route that always answers 403.
  const canUseRail = useCanUseTodoRail();
  // An SFC admin viewing as someone else must never write that person's
  // progress — nor their own from the surface they are watching. The write path
  // is gated on it explicitly, the same way `useRecordRoundVisit` is: leaving it
  // to the outbox being null was NOT equivalent, because "no outbox yet" is
  // also the boot case, and the pending latch below exists to replay exactly
  // that. So an observation made while impersonating sat stashed until the
  // session ended and then landed in the ADMIN's own checklist.
  const impersonating = viewAsUserId != null;
  const active = canUseRail && itemKey != null;
  const checklist = trpc.getRoundChecklist.useQuery(queryInput, {
    enabled: active,
  });
  // Queued, not fired: a mark made offline must survive until reconnect (and a
  // reload) rather than showing as done and vanishing. The outbox owns the
  // retry and the reconciliation against the server's answer.
  const sync = useTodoRailSync();

  // One predicate, shared with its test — see `shouldWatchForObservation`.
  // Cost of re-arming on a reset: un-marking while the surface is still on
  // screen re-ticks after the dwell. That is the honest answer, because at
  // that point the funder IS looking at it.
  const watching = shouldWatchForObservation({
    itemKey: active ? itemKey : null,
    impersonating,
    loaded: checklist.data != null,
    marks: checklist.data?.marks ?? [],
  });

  // The write itself, held in a ref so both the dwell timer and the imperative
  // `recordNow` fire exactly the same path without re-arming the timer effect
  // every render.
  const recordRef = useRef<() => void>(() => {});
  recordRef.current = () => {
    if (itemKey == null) return;

    // One rule, stated once and tested — see `dispositionForObservation`.
    const disposition = dispositionForObservation({
      hasOutbox: sync != null,
      loaded: checklist.data != null,
      watching,
      impersonating,
    });

    if (disposition !== "write") {
      pendingRef.current = disposition === "remember" ? itemKey : null;
      return;
    }

    const clientUpdatedAt = new Date().toISOString();

    utils.getRoundChecklist.setData(queryInput, (prev) => {
      if (prev == null) return prev;
      const rest = prev.marks.filter((mark) => mark.itemKey !== itemKey);
      return {
        ...prev,
        marks: [...rest, { itemKey, done: true, clientUpdatedAt }],
      };
    });

    // Non-null by the disposition above ("write" requires an outbox); narrowed
    // here because the check happened inside it.
    // Optional: absent while viewing as someone else, whose progress this
    // would be. Visits still record — see TodoRailSyncProvider.
    sync?.enqueueMark?.({ roundId, itemKey, done: true, clientUpdatedAt });
  };

  // `null` disables the timer entirely: some surfaces have no honest
  // time-based reading of "seen" and must wait for a deliberate signal.
  const dwellMs =
    options?.dwellMs === null
      ? null
      : (options?.dwellMs ?? TODO_OBSERVATION_DWELL_MS);

  const observe = options?.observe;
  const [onScreen, setOnScreen] = useState(observe == null);

  useEffect(() => {
    const element = observe?.current;
    if (observe == null) return;
    if (element == null) {
      setOnScreen(false);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry == null) return;
        // "Half the SECTION on screen, or half the SCREEN filled by it."
        //
        // A ratio alone can't express this: the funder's round notes are often
        // taller than the iPad viewport, and an element twice the viewport's
        // height can never reach a 0.5 ratio no matter how attentively it is
        // read — so a threshold-only rule silently switched the whole mark off
        // for exactly the longest content it exists to track.
        var viewportHeight = window.innerHeight || 0;
        var visibleHeight = entry.intersectionRect.height;
        setOnScreen(
          entry.isIntersecting &&
            (entry.intersectionRatio >= 0.5 ||
              (viewportHeight > 0 && visibleHeight >= viewportHeight * 0.5)),
        );
      },
      // Several thresholds so the callback re-fires as a long section scrolls
      // through, rather than only when it first touches the viewport.
      { threshold: [0, 0.1, 0.25, 0.5, 0.75] },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [observe, itemKey]);

  // Tab foregrounded and no drawer covering us — shared with the visit dwell.
  const attentive = usePageAttention(options?.hostDrawer);

  // A `recordNow` that fires before the checklist has loaded is REMEMBERED, not
  // dropped. The pyramid restores its expansion level from localStorage during
  // render and reports it from a mount effect, which runs before this hook's
  // query resolves — so a funder returning to a summary they had already
  // expanded to layer 3 reported it once, was ignored, and (the level never
  // changing again) never reported it again. The step could then only be
  // completed by collapsing and re-expanding.
  const pendingRef = useRef<string | null>(null);
  useEffect(() => {
    if (itemKey == null) return;
    // Nothing stashed survives an impersonated stretch, so ending one cannot
    // replay it. (`watching` is already false here, but the intent is worth
    // stating where the latch lives rather than inferring it two hops away.)
    if (impersonating) {
      pendingRef.current = null;
      return;
    }
    // A stash is only ever valid while the step is still unrecorded. Clearing
    // it here is the backstop for the rule above: once the checklist says done
    // — including because the funder just ticked it by hand — any remembered
    // report is stale and must not replay when they later un-tick.
    if (checklist.data != null && !watching) {
      pendingRef.current = null;
      return;
    }
    if (sync != null && pendingRef.current === itemKey) {
      pendingRef.current = null;
      recordRef.current();
    }
  }, [watching, itemKey, sync, checklist.data, impersonating]);

  useEffect(() => {
    if (itemKey == null || !watching || dwellMs == null) return;
    if (!onScreen || !attentive) return;

    // Cleared on unmount, so leaving before the dwell elapses records nothing —
    // which is exactly what makes a scroll-by different from a read.
    const timer = setTimeout(() => recordRef.current(), dwellMs);
    return () => clearTimeout(timer);
  }, [itemKey, watching, dwellMs, onScreen, attentive, sync]);

  // Deliberate engagement beats the clock: a reader who expands a pyramid to
  // its third layer has plainly read it, whether or not three seconds have
  // passed. Stable identity so callers can put it in their own deps.
  const recordNow = useCallback(() => recordRef.current(), []);

  return { recordNow };
}
